import { useEffect, useState } from 'react'
import {
    IBuildingOption,
    IDynamicHierarchyState,
    IFacilityMonitoringSettingsType,
    IHierarchyLevelConfig,
} from '../interfaces'
import {
    setIsFMApplicationEnabled,
    setHierarchyConfig,
    setHierarchyData,
    setSiteList,
    setSiteEntityTemplateId,
    setBuildingEntityTemplateId,
    setAssetEntityTemplateId,
    setAssetXCoordinateAttributeId,
    setAssetYCoordinateAttributeId,
    setBuildingNameAttributeId,
    setBuildingRelations,
    setAssetRelations,
    setSensorEntityTemplateId,
    setSensorReadingAttributeId,
    setBuildingFloorLayoutAttributeId,
    setSiteLocationAttributeId,
    setSiteNameAttributeId,
    setScheduledReportsEntityTemplateId,
    setAnalyticsDashboardId,
    setSiteBuildingAssetCount,
    setTopLevelBuildings,
} from '../../../../redux/slices/facilityMonitoringSlice'
import {
    fetchEntityRecordsList,
    getBuildingsAssetsCount,
} from '../../../../services/entityServices'
import {
    IEntityTableDefinition,
    ISiteBuildingAssetCount,
} from '../../../Entity/interfaces'
import { useAppDispatch, useAppSelector } from '../../../../redux/hook'
import { fetchRelations } from '../../../../services/dtServices'
import { IRelation } from '../../../DTStudio/interfaces'
import {
    getApplicationSettings,
    ISettingsResponse,
} from '../../../../services/applications'

import { prepareSiteList } from '../Utils/FMUtils'
import { Applications, EntityStatus } from '../../../../constants/constants'
interface IFMAppStatus {
    loadingFMApp: boolean
    errorInLoading: boolean
    isFMAppEnabled: boolean
    isReportsEnabled: boolean
    isDashboardEnabled: boolean
}

const useFMAppInitializer = (): IFMAppStatus => {
    const org = useAppSelector((state) => state.org.org)
    const orgId = org?.id ? org.id : ''
    const settingsVersion = useAppSelector(
        (state) => state.facilityMonitoring.settingsVersion,
    )
    const dispatch = useAppDispatch()
    const [fmAppStatus, setFMAppStatus] = useState<IFMAppStatus>({
        loadingFMApp: true,
        errorInLoading: false,
        isFMAppEnabled: false,
        isReportsEnabled: false,
        isDashboardEnabled: false,
    })

    useEffect(() => {
        const loadData = async () => {
            setFMAppStatus({
                loadingFMApp: true,
                errorInLoading: false,
                isFMAppEnabled: false,
                isReportsEnabled: false,
                isDashboardEnabled: false,
            })
            const fmAppSettingsResponse: ISettingsResponse<IFacilityMonitoringSettingsType> =
                await getApplicationSettings(
                    orgId,
                    Applications.facilityMonitoring.id,
                )
            const settings = fmAppSettingsResponse?.appSettings

            if (!settings) {
                setFMAppStatus({
                    loadingFMApp: false,
                    errorInLoading: true,
                    isFMAppEnabled: false,
                    isReportsEnabled: false,
                    isDashboardEnabled: false,
                })
                return
            }
            const isFMApplicationEnabled = settings.enable === 'true'
            const analyticsDashboardId = settings.analyticsDashboardId
            const scheduledReportsEntityTemplateId =
                settings.scheduledReportsEntityTemplateId
            const sensorEntityTemplateId = settings.sensorEntityTemplateId
            const sensorReadingAttributeId = settings.sensorReadingAttributeId
            const buildingEntityTemplateId = settings.buildingEntityTemplateId
            const assetEntityTemplateId = settings.assetEntityTemplateId
            const buildingNameAttributeId = settings.buildingNameAttributeId
            const buildingFloorLayoutAttributeId =
                settings.buildingFloorLayoutAttributeId
            const assetXCoordinateAttributeId =
                settings.assetXCoordinateAttributeId
            const assetYCoordinateAttributeId =
                settings.assetYCoordinateAttributeId
            let isReportsEnabled = false
            if (scheduledReportsEntityTemplateId) {
                dispatch(
                    setScheduledReportsEntityTemplateId(
                        scheduledReportsEntityTemplateId,
                    ),
                )
                isReportsEnabled = true
            }

            let isDashboardEnabled = false
            if (analyticsDashboardId) {
                dispatch(setAnalyticsDashboardId(analyticsDashboardId))
                isDashboardEnabled = true
            }

            let preBuildingHierarchyLevels: IHierarchyLevelConfig[] = []
            const hierarchy = fmAppSettingsResponse?.hierarchy
            const numLevels = Number.parseInt(
                settings.numberOfLevelsBeforeBuilding || '0',
                10,
            )
            if (hierarchy && hierarchy.length > 0 && numLevels > 0) {
                const startIndex = Math.max(0, hierarchy.length - numLevels)
                const activeHierarchy = hierarchy.slice(startIndex)
                preBuildingHierarchyLevels = activeHierarchy.map((h, index) => {
                    const isLast = index === activeHierarchy.length - 1
                    return {
                        id: `level_${index}`,
                        name: h.name,
                        entityTemplateId: h.entityTemplateId,
                        isMapLevel: isLast,
                        locationAttributeId: isLast
                            ? settings.siteLocationAttributeId
                            : undefined,
                        nameAttributeId: isLast
                            ? settings.siteNameAttributeId
                            : undefined,
                    }
                })
            }

            if (
                !isFMApplicationEnabled ||
                !sensorEntityTemplateId ||
                !buildingEntityTemplateId ||
                !assetEntityTemplateId ||
                !buildingNameAttributeId ||
                !buildingFloorLayoutAttributeId ||
                !assetXCoordinateAttributeId ||
                !assetYCoordinateAttributeId ||
                !sensorReadingAttributeId
            ) {
                setFMAppStatus({
                    loadingFMApp: false,
                    errorInLoading: true,
                    isFMAppEnabled: isFMApplicationEnabled,
                    isReportsEnabled,
                    isDashboardEnabled,
                })
                return
            }

            const queryParams = {
                filterBy: [EntityStatus.ACTIVE],
                filters: [],
            }

            // Fetch top level data and all relations dynamically
            const fetchPromises: Promise<any>[] = []

            // 1. Fetch top level entities (if any dynamic levels exist)
            if (preBuildingHierarchyLevels.length > 0) {
                const topLevelConfig = preBuildingHierarchyLevels[0]
                fetchPromises.push(
                    fetchEntityRecordsList(
                        orgId,
                        topLevelConfig.entityTemplateId,
                        queryParams,
                    ),
                )
            } else {
                // If no levels before building, building is the top level
                fetchPromises.push(
                    fetchEntityRecordsList(
                        orgId,
                        buildingEntityTemplateId,
                        queryParams,
                    ),
                )
            }

            // 2. Fetch relations for all dynamic levels
            for (const config of preBuildingHierarchyLevels) {
                fetchPromises.push(
                    fetchRelations(config.entityTemplateId, orgId),
                )
            }

            // 3. Fetch relations for static building and asset
            fetchPromises.push(
                fetchRelations(buildingEntityTemplateId, orgId),
                fetchRelations(assetEntityTemplateId, orgId),
                getBuildingsAssetsCount(orgId, true),
            )

            const results = await Promise.all(fetchPromises)

            let resultIndex = 0
            const topLevelRecords = results[
                resultIndex++
            ] as IEntityTableDefinition

            const hierarchyData: Record<number, IDynamicHierarchyState> = {}

            for (let i = 0; i < preBuildingHierarchyLevels.length; i++) {
                hierarchyData[i] = {
                    levelIndex: i,
                    config: preBuildingHierarchyLevels[i],
                    entities:
                        i === 0
                            ? topLevelRecords.data.map(
                                  (item) =>
                                      ({
                                          id: item.entityId,
                                          entityInstanceId: item.entityId,
                                          templateId:
                                              preBuildingHierarchyLevels[i]
                                                  .entityTemplateId,
                                          attributes: item.attributes,
                                          displayName: item.uid || '',
                                          uid: item.uid || '',
                                          status: item.status,
                                          permissionLevel: item.action,
                                      }) as unknown as any,
                              )
                            : [],
                    selectedUID: '',
                    relations: results[resultIndex++] as IRelation[],
                }
            }

            const buildingRelations = results[resultIndex++] as IRelation[]
            const assetRelations = results[resultIndex++] as IRelation[]
            const siteBuildingAssetCount = results[
                resultIndex++
            ] as ISiteBuildingAssetCount[]

            dispatch(setIsFMApplicationEnabled(isFMApplicationEnabled))

            // Dispatch Dynamic Hierarchy State
            dispatch(setHierarchyConfig(preBuildingHierarchyLevels))
            dispatch(setHierarchyData(hierarchyData))

            // When there are 0 levels before building, buildings are top-level.
            // Produce lean IBuildingOption[] — no IAttribute[] vs Record mismatch.
            if (preBuildingHierarchyLevels.length === 0) {
                const mappedBuildings: IBuildingOption[] =
                    topLevelRecords.data.map((item) => ({
                        id: item.attributes['Id'],
                        entityInstanceId: item.entityId,
                        uid: item.uid || '',
                        name: item.uid || '',
                        // item.attributes is Record<string,any> (flat {attrId: value})
                        layout: item.attributes?.[
                            buildingFloorLayoutAttributeId
                        ] as string | undefined,
                        buildingName: item.attributes?.[
                            buildingNameAttributeId
                        ] as string | undefined,
                        // No licenseDuration attributeId in settings yet — leave undefined
                        licenseDuration: undefined,
                    }))
                dispatch(setTopLevelBuildings(mappedBuildings))
            } else {
                dispatch(setTopLevelBuildings([]))
            }

            // Map View Legacy Integration
            const mapLevelConfig =
                preBuildingHierarchyLevels.find((l) => l.isMapLevel) ||
                preBuildingHierarchyLevels[0]
            if (mapLevelConfig) {
                dispatch(
                    setSiteEntityTemplateId(mapLevelConfig.entityTemplateId),
                )
                dispatch(
                    setSiteLocationAttributeId(
                        mapLevelConfig.locationAttributeId || '',
                    ),
                )
                dispatch(
                    setSiteNameAttributeId(
                        mapLevelConfig.nameAttributeId || '',
                    ),
                )

                // If Map level is the top level, we can populate siteList immediately.
                if (preBuildingHierarchyLevels[0] === mapLevelConfig) {
                    const siteList = prepareSiteList(
                        topLevelRecords,
                        mapLevelConfig.locationAttributeId || '',
                        mapLevelConfig.nameAttributeId || '',
                    )
                    dispatch(setSiteList(siteList))
                } else {
                    // For now, empty list until user drills down, or we could fetch map level directly if needed.
                    // But Map View requires all sites. Map View might need to fetch map level records on its own.
                    const siteRecords = await fetchEntityRecordsList(
                        orgId,
                        mapLevelConfig.entityTemplateId,
                        queryParams,
                    )
                    const siteList = prepareSiteList(
                        siteRecords,
                        mapLevelConfig.locationAttributeId || '',
                        mapLevelConfig.nameAttributeId || '',
                    )
                    dispatch(setSiteList(siteList))
                }
            } else {
                dispatch(setSiteList([]))
            }

            dispatch(setSiteBuildingAssetCount(siteBuildingAssetCount))

            //Building State Action Dispatchers
            dispatch(setBuildingRelations(buildingRelations))
            dispatch(setBuildingEntityTemplateId(buildingEntityTemplateId))
            dispatch(setBuildingNameAttributeId(buildingNameAttributeId))
            dispatch(
                setBuildingFloorLayoutAttributeId(
                    buildingFloorLayoutAttributeId,
                ),
            )

            //Asset State Action Dispatchers
            dispatch(setAssetRelations(assetRelations))
            dispatch(setAssetEntityTemplateId(assetEntityTemplateId))
            dispatch(
                setAssetXCoordinateAttributeId(assetXCoordinateAttributeId),
            )
            dispatch(
                setAssetYCoordinateAttributeId(assetYCoordinateAttributeId),
            )

            //Sensor State Action Dispatchers
            dispatch(setSensorEntityTemplateId(sensorEntityTemplateId))
            dispatch(setSensorReadingAttributeId(sensorReadingAttributeId))

            //Other
            setFMAppStatus({
                loadingFMApp: false,
                errorInLoading: false,
                isFMAppEnabled: isFMApplicationEnabled,
                isReportsEnabled,
                isDashboardEnabled,
            })
        }

        loadData()
    }, [orgId, dispatch, settingsVersion])
    return fmAppStatus
}

export default useFMAppInitializer
